import React from 'react';

/**
 * Optional patient demographics collected alongside a scan upload.
 *
 * None of ZemedicAI's vision models take these as literal model inputs (they
 * are pure image classifiers) — but age, sex, and diabetes history materially
 * affect clinical interpretation (melanoma risk rises with age; diabetic
 * retinopathy and foot ulcer risk scale with diabetes duration), so they are
 * threaded into Zafya AI's clinician-facing note and stored with the record
 * to enrich future ZemedicLabel training data. All fields are optional —
 * analysis works fully without them.
 */
const DemographicsFields = ({ value, onChange, showDiabetesFields = false }) => {
  const set = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    onChange({ ...value, [field]: v });
  };

  return (
    <details className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 group">
      <summary className="cursor-pointer text-sm font-medium text-gray-300 select-none">
        Patient details <span className="text-gray-500 font-normal">(optional — improves clinical context)</span>
      </summary>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Age</label>
          <input
            type="number"
            min="0"
            max="120"
            value={value.age || ''}
            onChange={set('age')}
            placeholder="e.g. 58"
            className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Sex</label>
          <select
            value={value.sex || ''}
            onChange={set('sex')}
            className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 focus:outline-none focus:border-purple-500"
          >
            <option value="">Prefer not to say</option>
            <option value="Female">Female</option>
            <option value="Male">Male</option>
          </select>
        </div>
        {showDiabetesFields && (
          <>
            <div className="col-span-2 flex items-center gap-2">
              <input
                id="is_known_diabetic"
                type="checkbox"
                checked={!!value.isKnownDiabetic}
                onChange={set('isKnownDiabetic')}
                className="rounded border-gray-700 bg-gray-900 text-purple-600 focus:ring-purple-500"
              />
              <label htmlFor="is_known_diabetic" className="text-xs text-gray-400">
                Known diabetic
              </label>
            </div>
            {value.isKnownDiabetic && (
              <div className="col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Years with diabetes</label>
                <input
                  type="number"
                  min="0"
                  max="80"
                  value={value.diabetesDurationYears || ''}
                  onChange={set('diabetesDurationYears')}
                  placeholder="e.g. 12"
                  className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-1.5 text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-500"
                />
              </div>
            )}
          </>
        )}
      </div>
    </details>
  );
};

export default DemographicsFields;
